import React, { useState } from 'react';
import { FaDownload, FaCalendarAlt, FaBox, FaLayerGroup, FaCheckCircle, FaFileAlt, FaTrash } from 'react-icons/fa';
import './ContentMetadata.css';

const statusIcons = {
  PUBLISHED: <FaCheckCircle className="status-icon published" />,
  DRAFT: <FaFileAlt className="status-icon draft" />,
  ARCHIVED: <FaBox className="status-icon archived" />
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const ContentMetadata = ({ data = [], onDelete }) => {
  const [deleteId, setDeleteId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async (id) => {
    setDeleteId(id);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    
    setIsDeleting(true);
    try {
      await onDelete(deleteId);
    } catch (error) {
      console.error('Delete failed:', error);
    } finally {
      setIsDeleting(false);
      setDeleteId(null);
    }
  };

  const cancelDelete = () => {
    setDeleteId(null);
  };

  

  return (
    <div className="database-viewer-table">
      {/* Delete Confirmation Modal */}
      {deleteId && (
        <div className="delete-modal">
          <div className="delete-modal-content">
            <h3>Confirm Deletion</h3>
            <p>Are you sure you want to delete this content entry? This action cannot be undone.</p>
            <div className="delete-modal-actions">
              <button 
                onClick={cancelDelete} 
                className="cancel-button"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button 
                onClick={confirmDelete} 
                className="delete-button"
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete Permanently'}
              </button>
            </div>
          </div>
        </div>
      )}

      {data.length > 0 ? (
        <table className="styled-table">
          <thead>
            <tr>
              <th>Thumbnail</th>
              <th>Content Details</th>
              <th>Metadata</th>
              <th>Status</th>
              <th>Downloads</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.map((item) => (
              <tr key={item.id}>
                <td className="thumbnail-cell">
                  {item.thumbnailUrl && !item.thumbnailUrl.includes('undefined') ? (
                    <img 
                      src={item.thumbnailUrl} 
                      alt={`${item.name} thumbnail`} 
                      className="thumbnail-image"
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/100x100?text=No+Thumbnail';
                      }}
                    />
                  ) : (
                    <div className="thumbnail-placeholder">
                      <FaBox className="placeholder-icon" />
                    </div>
                  )}
                </td>
                <td className="content-details">
                  <h4 className="content-name">{item.name}</h4>
                  <p className="content-description">{item.description}</p>
                  <div className="content-meta">
                    <span className="content-version">v{item.version}</span>
                    <span className="content-utility">{item.utility}</span>
                    <span className="content-category">{item.category}</span>
                  </div>
                </td>
                <td className="metadata-cell">
                  <div className="metadata-item">
                    <FaCalendarAlt className="metadata-icon" />
                    <span>Released: {formatDate(item.releaseDate)}</span>
                  </div>
                  <div className="metadata-item">
                    <FaLayerGroup className="metadata-icon" />
                    <span>Type: {item.contentType}</span>
                  </div>
                  <div className="metadata-item">
                    <span>Size: {item.fileSize}</span>
                  </div>
                  <div className="metadata-item">
                    <span>ID: <span className="monospace">{item.id}</span></span>
                  </div>
                </td>
                <td className="status-cell">
                  {statusIcons[item.status]}
                  <span>{item.status}</span>
                  {item.notes && (
                    <div className="content-notes">
                      <small>Notes: {item.notes}</small>
                    </div>
                  )}
                </td>
                <td className="downloads-cell">
                  <div className="downloads-count">
                    <FaDownload className="downloads-icon" />
                    <span>{item.downloads}</span>
                  </div>
                </td>
                <td className="actions-cell">
                  <a 
                    href={item.downloadUrl} 
                    className="action-button download"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FaDownload /> Download
                  </a>
                  <button 
                    className="action-button delete"
                    onClick={() => handleDelete(item.id)}
                  >
                    <FaTrash /> Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="no-data-message">
          No content metadata available
        </div>
      )}
    </div>
  );
};

export default ContentMetadata;